import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search as SearchIcon, MapPin } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import PropertyTypeSelector from "./PropertyTypeSelector";
import { usePropertyTypes } from "@/hooks/usePropertyTypes";
import { useProvinces } from "@/hooks/useProvinces";
import { useDistricts } from "@/hooks/useDistricts";
import { useCities } from "@/hooks/useCities";
import { ListingType, Province, District, City } from "@/lib/types";
import { cn } from "@/lib/utils";
import { useSearch } from "@/context/SearchContext";

// Define listing type options
const listingTypeOptions: { value: ListingType; label: string }[] = [
  { value: "rent", label: "Rent" },
  { value: "sale", label: "Sale" },
  { value: "lease", label: "Lease" },
];

interface LocationSuggestion {
  id: number;
  name: string; // Display name
  slug: string; // Slug for backend
  type: "province" | "district" | "city";
}

const SearchForm = () => {
  const [listingType, setListingType] = useState<ListingType>("rent");
  const [propertyType, setPropertyType] = useState<string>("all");
  const [keyword, setKeyword] = useState("");
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [selectedLocationSlug, setSelectedLocationSlug] = useState<
    string | undefined
  >(undefined);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);

  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const { handleFilterChange, handleSearch } = useSearch();

  const {
    propertyTypeOptions,
    propertyTypes,
    isLoading: propertyTypesLoading,
  } = usePropertyTypes();
  const { data: provinces, isLoading: provincesLoading } = useProvinces();
  const { data: districts, isLoading: districtsLoading } = useDistricts();
  const { data: cities, isLoading: citiesLoading } = useCities();

  const allLocations: LocationSuggestion[] = React.useMemo(() => {
    const locations: LocationSuggestion[] = [];
    provinces?.forEach((p) =>
      locations.push({
        id: p.id,
        name: `${p.name} (Province)`, // Make name more specific
        slug: p.slug || "",
        type: "province",
      })
    );
    districts?.forEach((d) =>
      locations.push({
        id: d.id,
        name: `${d.name} (District)`, // Make name more specific
        slug: d.slug || "",
        type: "district",
      })
    );
    cities?.forEach((c) =>
      locations.push({
        id: c.id,
        name: `${c.display_name || c.name} (City)`, // Make name more specific
        slug: c.slug || "",
        type: "city",
      })
    );
    return locations.filter((loc) => loc.slug); // Ensure slug exists
  }, [provinces, districts, cities]);

  useEffect(() => {
    if (keyword.trim() === "") {
      setSuggestions([]);
      setSelectedLocationSlug(undefined);
      return;
    }

    const lowercasedKeyword = keyword.toLowerCase();
    const filtered = allLocations.filter(
      (location) =>
        location.name.toLowerCase().includes(lowercasedKeyword) ||
        location.slug.toLowerCase().includes(lowercasedKeyword)
    );
    setSuggestions(filtered);
    setActiveSuggestionIndex(-1); // Reset active suggestion on new keyword
  }, [keyword, allLocations]);

  const handleListingTypeChange = (value: string) => {
    setListingType(value as ListingType);
  };

  const handleSelectSuggestion = (suggestion: LocationSuggestion) => {
    setKeyword(suggestion.name);
    setSelectedLocationSlug(suggestion.slug);
    setShowSuggestions(false);
    setSuggestions([]); // Clear suggestions after selection
    inputRef.current?.focus(); // Keep focus on input
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();

    // Default listing_type to "sale" if not selected
    const finalListingType = listingType || "rent";
    params.append("listing_type", finalListingType);

    console.log("Current propertyType state:", propertyType);
    console.log("Available propertyTypes for lookup:", propertyTypes);

    // Find the selected property type name
    let propertyTypeName: string | undefined;
    if (propertyType && propertyType !== "all") {
      const selectedProperty = propertyTypes.find(
        (type: any) => String(type.id) === propertyType
      );
      console.log("Found selectedProperty:", selectedProperty);
      if (selectedProperty && selectedProperty.name) {
        propertyTypeName = selectedProperty.name;
      }
    }

    if (propertyTypeName) {
      params.append("propertyType", propertyTypeName);
    } else if (propertyType && propertyType !== "all") {
      // Fallback if name not found, but propertyType is selected
      console.warn(
        "Property type name not found, falling back to ID:",
        propertyType
      );
      params.append("propertyType", propertyType);
    }

    if (selectedLocationSlug) {
      params.append("location", selectedLocationSlug);
    } else if (keyword.trim()) {
      params.append("search_term", keyword.trim());
    }

    params.append("page", "1");

    navigate(`/properties?${params.toString()}`);
  };

  const getTitle = () => {
    const selectedOption = listingTypeOptions.find(
      (opt) => opt.value === listingType
    );
    return `Commercial Properties for ${
      selectedOption ? selectedOption.label : "Search"
    }`;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault(); // Prevent cursor movement in input
      setActiveSuggestionIndex((prevIndex) =>
        Math.min(prevIndex + 1, suggestions.length - 1)
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault(); // Prevent cursor movement in input
      setActiveSuggestionIndex((prevIndex) => Math.max(prevIndex - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault(); // Prevent form submission
      if (activeSuggestionIndex > -1 && suggestions[activeSuggestionIndex]) {
        handleSelectSuggestion(suggestions[activeSuggestionIndex]);
      } else {
        handleSubmit(e); // Submit form if no suggestion is active
      }
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
      setActiveSuggestionIndex(-1);
    }
  };

  const handleInputFocus = () => {
    setShowSuggestions(true);
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Delay hiding to allow click on suggestions
    setTimeout(() => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.relatedTarget as Node)
      ) {
        setShowSuggestions(false);
      }
    }, 100);
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="text-center mb-6">
        <h2 className="text-3xl md:text-4xl font-semibold text-white flex items-center justify-center">
          <MapPin className="mr-3 h-8 w-8 text-propertylk-cyan" />
          {getTitle()}
        </h2>
      </div>
      <form
        onSubmit={handleSubmit}
        className="bg-white p-4 shadow-2xl rounded-lg flex flex-col md:flex-row items-center gap-3 md:gap-2 w-full"
      >
        {/* Listing Type Dropdown */}
        <div className="w-full md:w-1/4">
          <Select value={listingType} onValueChange={handleListingTypeChange}>
            <SelectTrigger className="h-12 text-base border-gray-300 rounded-md">
              <SelectValue placeholder="Select Type" />
            </SelectTrigger>
            <SelectContent>
              {listingTypeOptions.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  className="text-base"
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Property Type Dropdown */}
        <div className="w-full md:w-1/4">
          <PropertyTypeSelector
            propertyTypes={propertyTypeOptions || []}
            loading={propertyTypesLoading}
            propertyType={propertyType}
            onPropertyTypeChange={setPropertyType}
            triggerClassName="h-12 text-base border-gray-300 rounded-md w-full"
          />
        </div>

        {/* Keyword Input with Autocomplete */}
        <div className="relative flex-grow w-full md:w-1/2">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            ref={inputRef}
            type="text"
            placeholder="Province, Districts, or Cities"
            value={keyword}
            onChange={(e) => {
              setKeyword(e.target.value);
              setSelectedLocationSlug(undefined); // Clear selected slug on new input
            }}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            className="h-12 pl-10 pr-4 text-base w-full border-gray-300 rounded-md"
          />
          {showSuggestions && suggestions.length > 0 && (
            <div
              ref={suggestionsRef}
              className="absolute z-10 w-full bg-white border border-gray-200 rounded-md shadow-lg mt-1 max-h-60 overflow-y-auto"
            >
              {suggestions.map((suggestion, index) => (
                <div
                  key={`${suggestion.type}-${suggestion.id}`}
                  className={cn(
                    "px-4 py-2 cursor-pointer hover:bg-gray-100",
                    activeSuggestionIndex === index && "bg-gray-100"
                  )}
                  onMouseDown={(e) => e.preventDefault()} // Prevent input blur on click
                  onClick={() => handleSelectSuggestion(suggestion)}
                >
                  {suggestion.name}{" "}
                  <span className="text-gray-500 text-sm">
                    ({suggestion.type})
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Search Button */}
        <Button
          type="submit"
          className="h-12 bg-cyan-600 hover:bg-cyan-700 text-white text-lg font-semibold w-full md:w-auto px-8"
        >
          Search
        </Button>
      </form>
    </div>
  );
};

export default SearchForm;
